/**
 * The permissions a role can carry, and which of them the role page offers.
 * Mirrors the old UI's permission catalogue and its filtering rules: what is
 * offered depends on the network's platforms, the role's application, type
 * and scope.
 */

import type { Network } from '@/services/networks/networksApi'

/** INTERNAL roles are for the network's own staff; EXTERNAL ones for self-service advertisers. */
export type RoleType = 'INTERNAL' | 'EXTERNAL'
export type RoleScope = 'NETWORK' | 'TEAM'

interface PermissionInfo {
  apiKey: string
  scope: RoleScope
  application: string
  /** Unset means the permission fits either role type. */
  roleType?: RoleType
  /** The platform the network must run for the permission to be offered. */
  platform: 'ADVERTISING' | 'MARKETPLACE' | 'SELF_SERVICE' | 'DATA' | 'POLITICAL'
  /** Only offered on marketplace networks. */
  onlyInMarketplace?: boolean
  /** Only offered when the network has advanced logging. */
  needsAdvancedLogging?: boolean
  /** Only offered to Adnuntius super admins for now. */
  superAdminOnly?: boolean
}

export const PERMISSIONS: PermissionInfo[] = [
  {
    apiKey: 'MANAGE_ADVERTISING',
    scope: 'TEAM',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'ADVERTISING',
  },
  {
    apiKey: 'MANAGE_BUSINESS',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    platform: 'ADVERTISING',
  },
  {
    apiKey: 'MANAGE_LAYOUTS',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    platform: 'ADVERTISING',
  },
  {
    apiKey: 'MANAGE_PUBLISHING',
    scope: 'TEAM',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'ADVERTISING',
  },
  {
    apiKey: 'AX_PUBLISHER',
    scope: 'TEAM',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'MARKETPLACE',
  },
  {
    apiKey: 'AX_ADVERTISER',
    scope: 'TEAM',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'MARKETPLACE',
  },
  {
    apiKey: 'CREATIVE_AGENCY',
    scope: 'TEAM',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'MARKETPLACE',
  },
  {
    apiKey: 'MANAGE_AUDIENCES',
    scope: 'TEAM',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'ADVERTISING',
    onlyInMarketplace: true,
  },
  {
    apiKey: 'AX_PUBLISHER_ADMIN',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'MARKETPLACE',
  },
  {
    apiKey: 'AX_ADVERTISER_ADMIN',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'MARKETPLACE',
  },
  {
    apiKey: 'AX_PUBLISHER_TEAM_ADMIN',
    scope: 'TEAM',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'MARKETPLACE',
    superAdminOnly: true,
  },
  {
    apiKey: 'AX_ADVERTISER_TEAM_ADMIN',
    scope: 'TEAM',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'MARKETPLACE',
    superAdminOnly: true,
  },
  {
    apiKey: 'MANAGE_REPORT_TEMPLATES',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    platform: 'ADVERTISING',
  },
  {
    apiKey: 'MANAGE_NETWORK_DASHBOARDS',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    platform: 'ADVERTISING',
  },
  {
    apiKey: 'SELF_SERVICE_USER_ADMIN',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    roleType: 'EXTERNAL',
    platform: 'SELF_SERVICE',
  },
  {
    apiKey: 'MANAGE_SYSTEM',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'ADVERTISING',
  },
  {
    apiKey: 'DOWNLOAD_LOGS',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'ADVERTISING',
    needsAdvancedLogging: true,
  },
  { apiKey: 'RUN_REPORTS', scope: 'TEAM', application: 'ADVERTISING', platform: 'ADVERTISING' },
  {
    apiKey: 'SELF_SERVICE_OWN_ADVERTISING',
    scope: 'TEAM',
    application: 'ADVERTISING',
    roleType: 'EXTERNAL',
    platform: 'SELF_SERVICE',
  },
  {
    apiKey: 'VIEW_ALL_OBJECTS',
    scope: 'NETWORK',
    application: 'ADVERTISING',
    roleType: 'INTERNAL',
    platform: 'ADVERTISING',
  },
  {
    apiKey: 'DATA_ADMIN',
    scope: 'NETWORK',
    application: 'DATA',
    roleType: 'INTERNAL',
    platform: 'DATA',
  },
  {
    apiKey: 'MANAGE_DATA',
    scope: 'TEAM',
    application: 'DATA',
    roleType: 'INTERNAL',
    platform: 'DATA',
  },
  {
    apiKey: 'POLITICAL_ADMIN',
    scope: 'NETWORK',
    application: 'POLITICAL',
    roleType: 'INTERNAL',
    platform: 'POLITICAL',
  },
  {
    apiKey: 'CONTROLLER',
    scope: 'TEAM',
    application: 'POLITICAL',
    roleType: 'INTERNAL',
    platform: 'POLITICAL',
  },
  {
    apiKey: 'SPONSOR',
    scope: 'TEAM',
    application: 'POLITICAL',
    roleType: 'INTERNAL',
    platform: 'POLITICAL',
  },
]

const byKey = new Map(PERMISSIONS.map((p) => [p.apiKey, p]))

/** Whether the network offers self-service roles, which brings in the role type choice. */
export const hasRoleTypes = (network: Network | null) => !!network?.suiPlatform

/** The type of a role: EXTERNAL when it carries any external-only permission. */
export function roleTypeOf(application: string, permissions: string[]): RoleType {
  if (application === 'POLITICAL') return 'INTERNAL'
  return permissions.some((key) => byKey.get(key)?.roleType === 'EXTERNAL')
    ? 'EXTERNAL'
    : 'INTERNAL'
}

interface RoleShape {
  application: string
  roleType: RoleType
  scope: RoleScope
}

const fits = (p: PermissionInfo, role: RoleShape) =>
  p.application === role.application &&
  p.scope === role.scope &&
  (!p.roleType || p.roleType === role.roleType)

/**
 * The permissions offered for a role of the given shape, plus any the role
 * already has of that shape that the network would no longer offer.
 */
export function availablePermissions(
  network: Network | null,
  role: RoleShape,
  assigned: string[],
  superAdmin: boolean
): string[] {
  const platforms = new Set<string>()
  if (network?.advertisingPlatform) platforms.add('ADVERTISING')
  if (network?.dataPlatform) platforms.add('DATA')
  if (network?.suiPlatform) platforms.add('SELF_SERVICE')
  if (network?.marketplacePlatform) platforms.add('MARKETPLACE')
  if (network?.politicalApp) platforms.add('POLITICAL')

  const offered = PERMISSIONS.filter(
    (p) =>
      fits(p, role) &&
      platforms.has(p.platform) &&
      // External roles are only offered the external-only permissions.
      (role.roleType === 'EXTERNAL'
        ? p.roleType === 'EXTERNAL'
        : (!p.onlyInMarketplace || !!network?.marketplacePlatform) &&
          (!p.needsAdvancedLogging || !!network?.advancedLogging))
  ).map((p) => p.apiKey)

  const extras = assigned.filter((key) => {
    const p = byKey.get(key)
    return p && !offered.includes(key) && fits(p, role)
  })

  return [...offered, ...extras].filter((key) => superAdmin || !byKey.get(key)?.superAdminOnly)
}

/** Drops the known permissions that don't fit the role's application, scope and type. */
export function cleanPermissions(permissions: string[], role: RoleShape): string[] {
  return permissions.filter((key) => {
    const p = byKey.get(key)
    return !p || fits(p, role)
  })
}
